import { Loader2, PhoneCall } from 'lucide-react';
import { useState } from 'react';

import { poOdmowie401 } from '../../lib/martwaSesja';
import { ZDANIE_MARTWEJ_SESJI } from '../../lib/nexusApi';
import { NEXUS_API_URL } from '../../lib/nexusApiCore';
import { supabase } from '../../lib/supabase';

import {
  MAX_NUMEROW_DIAGNOZY,
  TRASA_DIAGNOZY_TELEFONOW,
  numeryZPola,
  opiszKsztaltNumeru,
  opiszWynikDiagnozy,
  zdanieSygnalu,
  type WynikNumeruPowloki,
} from './wtyczkiManifest';

// =============================================================================
// „Diagnoza numerów" — sekcja „Połącz wtyczkę" dla programu lojalnościowego.
//
// Administrator wkleja numery klientek, które zgłosiły „nie widzę punktów /
// karty" albo „dostaję SMS mimo STOP", i dostaje dla każdego numeru listę
// kontaktów GoCRM, które ten numer znajduje w pięciu zapisach. Wyłącznie
// odczyt: przycisk nie zmienia niczego w GoCRM, a naprawa (przeniesienie
// karty, scalenie) zostaje decyzją człowieka poza tym ekranem.
//
// Pełne numery nie wracają z serwera — tylko kształt zapisu i cztery ostatnie
// cyfry. Kolejność wyników = kolejność wpisanych numerów (bez powtórzeń).
// =============================================================================

export function DiagnozaNumerow({ orgId }: { orgId: string }) {
  const [pole, setPole] = useState('');
  const [trwa, setTrwa] = useState(false);
  const [blad, setBlad] = useState<string | null>(null);
  const [wyniki, setWyniki] = useState<WynikNumeruPowloki[] | null>(null);

  const numery = numeryZPola(pole);
  const zaDuzo = numery.length > MAX_NUMEROW_DIAGNOZY;

  const diagnozuj = async () => {
    setTrwa(true);
    setBlad(null);
    setWyniki(null);
    try {
      const { data: sesja } = await supabase.auth.getSession();
      const token = sesja.session?.access_token;
      if (!token) throw new Error('Sesja wygasła — zaloguj się ponownie.');
      const res = await fetch(`${NEXUS_API_URL}${TRASA_DIAGNOZY_TELEFONOW}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ organization_id: orgId, numery }),
      });
      const cialo: unknown = await res.json().catch(() => null);
      if (!res.ok) {
        if (res.status === 401 && (await poOdmowie401('nexus-core'))) {
          throw new Error(ZDANIE_MARTWEJ_SESJI);
        }
        const opis = cialo as { error?: string; message?: string } | null;
        throw new Error(
          opis?.message || opis?.error || `Diagnoza nie powiodła się (${res.status}).`
        );
      }
      const wynik = opiszWynikDiagnozy(cialo);
      if ('blad' in wynik) setBlad(wynik.blad);
      else setWyniki(wynik.numery);
    } catch (err) {
      setBlad((err as Error).message || 'Diagnoza nie powiodła się.');
    } finally {
      setTrwa(false);
    }
  };

  return (
    <div className="space-y-1.5 pt-2 border-t border-white/10">
      <p className="text-xs text-gray-400 flex items-center gap-1.5">
        <PhoneCall className="w-3.5 h-3.5" />
        Diagnoza numerów klientów
      </p>
      <p className="text-xs text-gray-500">
        Wklej numery (jeden w wierszu, najwyżej {MAX_NUMEROW_DIAGNOZY}). Sprawdzamy, ile kontaktów
        GoCRM znajduje każdy z nich — tylko odczyt, nic się nie zmienia.
      </p>
      <textarea
        value={pole}
        onChange={(e) => setPole(e.target.value)}
        rows={4}
        aria-label="Numery telefonów do diagnozy"
        placeholder={'535 921 721\n+48 501 333 933'}
        className="w-full px-3 py-1.5 bg-gray-950 border border-gray-700 rounded-ids-md text-sm text-aurora-text focus:outline-none focus:border-indigo-500 font-mono"
      />
      {zaDuzo && (
        <p className="text-xs text-amber-300/90">
          {numery.length} numerów — podziel listę, najwyżej {MAX_NUMEROW_DIAGNOZY} naraz.
        </p>
      )}
      <button
        onClick={diagnozuj}
        disabled={trwa || numery.length === 0 || zaDuzo}
        className="w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-ids-md bg-indigo-500/10 text-indigo-300 hover:bg-indigo-500/20 disabled:opacity-50 text-sm transition-colors"
      >
        {trwa ? <Loader2 className="w-4 h-4 animate-spin" /> : <PhoneCall className="w-4 h-4" />}
        {trwa ? 'Sprawdzam numery…' : 'Diagnozuj numery'}
      </button>
      {blad && (
        <p role="alert" className="text-xs text-amber-300/90 break-words">
          {blad}
        </p>
      )}
      {wyniki && (
        <ul className="space-y-2" aria-label="Wyniki diagnozy numerów">
          {wyniki.map((w) => (
            <li key={w.numer} className="rounded-ids-md bg-white/5 px-3 py-2 space-y-1">
              <p className="text-sm font-mono text-indigo-300">{w.numer}</p>
              {w.sygnaly.length === 0 ? (
                <p className="text-xs text-emerald-300/90">Jeden kontakt, spójny zapis.</p>
              ) : (
                <ul className="space-y-0.5">
                  {w.sygnaly.map((s) => (
                    <li key={s} className="text-xs text-amber-300/90">
                      {zdanieSygnalu(s)}
                    </li>
                  ))}
                </ul>
              )}
              {w.kontakty.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs text-gray-300">
                    <thead>
                      <tr className="text-gray-500 text-left">
                        <th className="pr-2 font-normal">Kontakt</th>
                        <th className="pr-2 font-normal">Telefon</th>
                        <th className="pr-2 font-normal">Karty</th>
                        <th className="pr-2 font-normal">Punkty</th>
                        <th className="pr-2 font-normal">Transakcje</th>
                        <th className="font-normal">Zgoda SMS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {w.kontakty.map((k) => (
                        <tr key={k.contact_id} className="align-top">
                          <td className="pr-2 font-mono">{k.contact_id}</td>
                          <td className="pr-2 font-mono">{opiszKsztaltNumeru(k.telefon)}</td>
                          <td className="pr-2 font-mono">
                            {k.karty.length === 0
                              ? '—'
                              : k.karty.map((c) => opiszKsztaltNumeru(c)).join(', ')}
                          </td>
                          <td className="pr-2">{k.punkty ?? '?'}</td>
                          <td className="pr-2">{k.transakcje ?? '?'}</td>
                          <td>
                            {k.zgoda_marketingowa === null
                              ? '?'
                              : k.zgoda_marketingowa
                                ? 'tak'
                                : 'nie'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-[11px] text-gray-500">
                {w.warianty
                  .map((v) => `${v.wariant}: ${v.status === 200 ? v.liczba : `błąd ${v.status}`}`)
                  .join(' · ')}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
